// API client — REST + WebSocket helpers.
//
// Contract per design/API.html:
//   - Base URL is /v1 (proxied to api.nexusdijital.com in dev via vite.config)
//   - Session cookie auth on the dashboard
//   - Cursor-based pagination: { data: [...], next_cursor: string|null }
//   - RFC 7807 errors with a stable `code` field
//   - Idempotency-Key on creating POSTs
//
// Conventions used everywhere in this codebase:
//   - Endpoint paths NEVER include the /v1 prefix in calls — added by `api()`
//   - All POSTs/PATCHes auto-stringify a `body` option
//   - Throws `ApiError` (typed below) so callers can switch on `.code`

export type ApiErrorBody = {
  type?: string;
  title?: string;
  detail?: string;
  status?: number;
  instance?: string;
  /** Stable, machine-readable code for client switching. */
  code?: string;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: ApiErrorBody;
  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code;
    this.body = body;
  }
}

type ApiOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Send Idempotency-Key header on creating POSTs. */
  idempotencyKey?: string;
};

// In production: absolute "https://api.nexusdijital.com/v1" — cross-origin
// fetches with credentials: "include" ride the .nexusdijital.com cookie
// set by identity-service.
//
// In dev: relative "/v1" so the vite dev-server proxy can forward to
// the target URL (vite.config.ts `server.proxy`). Keeping it relative
// also avoids CORS in dev — same-origin from the browser's view.
const API_BASE = import.meta.env.DEV ? "/v1" : `${__API_BASE_URL__}/v1`;

export async function api<T = unknown>(
  path: string,
  opts: ApiOptions = {},
): Promise<T> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Accept-Language": (typeof document !== "undefined" ? document.documentElement.lang : "tr") || "tr",
    ...opts.headers,
  };
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.idempotencyKey) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }

  const resp = await fetch(url, {
    method: opts.method ?? "GET",
    credentials: "include",
    headers,
    body:
      opts.body instanceof FormData
        ? opts.body
        : opts.body !== undefined
          ? JSON.stringify(opts.body)
          : undefined,
    signal: opts.signal,
  });

  if (resp.status === 204) return undefined as T;
  const text = await resp.text();
  const data = text ? safeJSON(text) : undefined;

  if (!resp.ok) {
    const body = (data as ApiErrorBody | undefined) ?? undefined;
    throw new ApiError(
      body?.detail || body?.title || `HTTP ${resp.status}`,
      resp.status,
      body,
    );
  }
  return data as T;
}

function safeJSON(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
}

/**
 * Paginated GET returning { data, next_cursor }.
 *
 * Offset-paged collections (content-service v0.3.68: GET /v1/posts) send
 * the same envelope plus total/limit/offset/has_more, so those fields are
 * optional here rather than a separate type — endpoints that don't page
 * simply omit them and `next_cursor` stays null.
 */
export type CursorPage<T> = {
  data: T[];
  next_cursor: string | null;
  total?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
};

/** Tiny WebSocket helper. Reconnect logic lives in the hook layer. */
export function openWS(path: string): WebSocket {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return new WebSocket(`${proto}//${location.host}${path.startsWith("/") ? path : `/${path}`}`);
}
